import React, { useState, useEffect, useCallback } from 'react';
import { FeedbackDistribution, ComponentProps, FeedbackSource } from '../types.js';
import { getBaseUrl } from '../../../../../shared/utils/apiConfig';

/**
 * FeedbackDistributionChart - Platform Admin
 * 
 * RENAMED FROM: FeedbackOverview (vague name) → FeedbackDistributionChart (descriptive)
 * RECOVERED FROM: /src/features/advanced/components/feedback/insights/components/FeedbackOverview.tsx
 * MODIFICATIONS FOR PLATFORM ADMIN:
 * - Removed tenant_id filtering (platform-wide view)
 * - Uses platform admin token from localStorage
 * - Aggregates data across ALL tenants
 * - Added platform-wide impact calculations
 * 
 * Following 00-READ-THIS-FIRST:
 * - SINGLE SOURCE OF TRUTH: Recovered original logic, not duplicated
 * - NO FUNCTIONAL DUPLICATES: This is the only FeedbackDistributionChart
 * - DESCRIPTIVE NAMING: Shows distribution/trend charts, not vague "overview"
 * - CLEAN IMPORTS: Direct imports, no fallbacks
 */

// Platform admin API service (no tenant_id)
const apiService = {
  async get(url: string) {
    const token = localStorage.getItem('astrapeak_platform_admin_token');
    const response = await fetch(`${getBaseUrl()}/api${url}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    });
    if (!response.ok) throw new Error(`API Error: ${response.statusText}`);
    return response.json();
  }
};

// Enhanced feedback impact interfaces
interface FeedbackImpactMetrics {
  totalFeedback: number;
  accuracyImprovement: number;
  falsePositiveReduction: number;
  modelRetrainingCount: number;
  feedbackToRetrainRatio: number;
  averageResponseTime: number;
  impactScore: number;
}

interface RetrainingImpact {
  jobId: string;
  triggeredAt: string;
  status: 'triggered' | 'completed' | 'failed';
  beforeAccuracy: number;
  afterAccuracy: number;
  improvementPercent: number;
  feedbackCount: number;
}

// Impact calculation constants
const IMPACT_WEIGHTS = {
  ACCURACY: 0.4,
  FP_REDUCTION: 0.3,
  RETRAINING: 0.2,
  RESPONSE_TIME: 0.1,
} as const;

const BASELINE_METRICS = {
  FALSE_POSITIVE_RATE: 0.15, // 15% baseline FP rate
  MAX_RETRAINING_NORMALIZATION: 10, // Normalize to 10 retrainings
  MAX_RESPONSE_TIME_MS: 1000, // Normalize to 1 second
} as const;

// Backend API response types
interface RetrainingStats {
  accuracy?: number;
  false_positive_rate?: number;
  average_response_time?: number;
  total_feedback?: number;
}

interface RetrainingHistoryItem {
  job_id: string;
  trigger_time: string;
  status: 'triggered' | 'completed' | 'failed';
  trigger_stats?: RetrainingStats;
  result_stats?: RetrainingStats;
}

interface FeedbackStats {
  total?: number;
  false_positive_rate?: number;
  average_response_time?: number;
}

interface FeedbackOverviewProps extends ComponentProps {
  distribution: FeedbackDistribution;
  weeklyTrend: Array<{
    date: string;
    truePositive: number;
    falsePositive: number;
    needsInvestigation: number;
    source?: FeedbackSource;
  }>;
  source: FeedbackSource;
}

export const FeedbackDistributionChart: React.FC<FeedbackOverviewProps> = ({
  distribution,
  weeklyTrend,
  source
}) => {
  const [activeTab, setActiveTab] = useState<'distribution' | 'trend'>('distribution');
  const [impactMetrics, setImpactMetrics] = useState<FeedbackImpactMetrics | null>(null);
  const [retrainingHistory, setRetrainingHistory] = useState<RetrainingImpact[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Load feedback impact metrics (PLATFORM-WIDE, no tenant_id)
  const loadFeedbackImpact = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Get feedback statistics (aggregated across all tenants)
      const feedbackResponse = await apiService.get('/ml/feedback/stats');
      
      // Get retraining history (aggregated across all tenants)
      const retrainingResponse = await apiService.get('/ml/feedback/retraining-history?limit=10');
      
      if (feedbackResponse.status === 'success' && retrainingResponse.status === 'success') {
        // Calculate impact metrics
        const stats = feedbackResponse.data || {};
        const history = Array.isArray(retrainingResponse.data) ? retrainingResponse.data : [];
        
        const impactMetrics: FeedbackImpactMetrics = {
          totalFeedback: stats.total || 0,
          accuracyImprovement: calculateAccuracyImprovement(history),
          falsePositiveReduction: calculateFPReduction(stats),
          modelRetrainingCount: history.filter((h: RetrainingHistoryItem) => h.status === 'completed').length,
          feedbackToRetrainRatio: stats.total > 0 ? history.length / stats.total : 0,
          averageResponseTime: stats.average_response_time || 0,
          impactScore: calculateImpactScore(stats, history)
        };
        
        setImpactMetrics(impactMetrics);
        setRetrainingHistory(history.map((h: RetrainingHistoryItem) => ({
          jobId: h.job_id,
          triggeredAt: h.trigger_time,
          status: h.status,
          beforeAccuracy: h.trigger_stats?.accuracy || 0,
          afterAccuracy: h.result_stats?.accuracy || 0,
          improvementPercent: calculateImprovement(h.trigger_stats?.accuracy, h.result_stats?.accuracy),
          feedbackCount: h.trigger_stats?.total_feedback || 0
        })));
      }
    } catch (error) {
      console.error('Failed to load feedback impact:', error);
      setError('Failed to load feedback impact metrics');
    } finally {
      setLoading(false);
    }
  }, []);
  
  // Helper functions for impact calculations
  const calculateAccuracyImprovement = (history: RetrainingHistoryItem[]) => {
    const completedRetrains = history.filter(h => h.status === 'completed' && h.result_stats?.accuracy);
    if (completedRetrains.length === 0) return 0;
    
    const totalImprovement = completedRetrains.reduce((sum, h) => {
      const before = h.trigger_stats?.accuracy || 0;
      const after = h.result_stats?.accuracy || 0;
      const improvement = (after - before);
      return sum + improvement;
    }, 0);
    
    return totalImprovement / completedRetrains.length;
  };
  
  const calculateFPReduction = (stats: FeedbackStats) => {
    const fpRate = stats.false_positive_rate || 0;
    return Math.max(0, (BASELINE_METRICS.FALSE_POSITIVE_RATE - fpRate) / BASELINE_METRICS.FALSE_POSITIVE_RATE);
  };
  
  const calculateImpactScore = (stats: FeedbackStats, history: RetrainingHistoryItem[]) => {
    const completedRetrains = history.filter(h => h.status === 'completed');
    if (completedRetrains.length === 0) return 0;
    
    const h = completedRetrains[0];
    const accuracy = h.result_stats?.accuracy || 0;
    const fpRate = h.result_stats?.false_positive_rate || 0;
    const responseTime = h.result_stats?.average_response_time || 0;
    
    const accuracyScore = Math.min(1, accuracy);
    const fpScore = Math.max(0, 1 - fpRate);
    const retrainingScore = Math.min(1, history.length / BASELINE_METRICS.MAX_RETRAINING_NORMALIZATION);
    const responseScore = Math.max(0, 1 - (responseTime) / BASELINE_METRICS.MAX_RESPONSE_TIME_MS);
    
    return (
      accuracyScore * IMPACT_WEIGHTS.ACCURACY +
      fpScore * IMPACT_WEIGHTS.FP_REDUCTION +
      retrainingScore * IMPACT_WEIGHTS.RETRAINING +
      responseScore * IMPACT_WEIGHTS.RESPONSE_TIME
    );
  };
  
  const calculateImprovement = (before?: number, after?: number) => {
    if (!before || !after) return 0;
    return ((after - before) / before) * 100;
  };
  
  // Load impact data on component mount
  useEffect(() => {
    loadFeedbackImpact();
  }, [loadFeedbackImpact]);

  const filteredDistribution = source === 'all' 
    ? distribution 
    : {
        ...distribution,
        truePositive: distribution.source === source ? distribution.truePositive : 0,
        falsePositive: distribution.source === source ? distribution.falsePositive : 0,
        needsInvestigation: distribution.source === source ? distribution.needsInvestigation : 0
      };

  const filteredTrend = source === 'all'
    ? weeklyTrend
    : weeklyTrend.filter(w => w.source === source);

  const distributionData = [
    { name: 'True Positive', value: filteredDistribution.truePositive, color: 'bg-green-500' },
    { name: 'False Positive', value: filteredDistribution.falsePositive, color: 'bg-red-500' },
    { name: 'Needs Investigation', value: filteredDistribution.needsInvestigation, color: 'bg-blue-500' }
  ];

  const maxValue = Math.max(
    ...distributionData.map(d => d.value),
    ...filteredTrend.map(w => Math.max(w.truePositive, w.falsePositive, w.needsInvestigation))
  );

  return (
    <div className="bg-gray-800/50 rounded-lg p-6">
      <h3 className="text-lg font-semibold mb-4">Feedback Distribution</h3>
      
      <div className="flex gap-2 mb-4">
        <button
          className={`px-3 py-1 rounded-md ${
            activeTab === 'distribution' 
              ? 'bg-gray-700 text-white' 
              : 'text-gray-400 hover:text-white'
          }`}
          onClick={() => setActiveTab('distribution')}
        >
          Distribution
        </button>
        <button
          className={`px-3 py-1 rounded-md ${
            activeTab === 'trend' 
              ? 'bg-gray-700 text-white' 
              : 'text-gray-400 hover:text-white'
          }`}
          onClick={() => setActiveTab('trend')}
        >
          Weekly Trend
        </button>
      </div>

      {activeTab === 'distribution' ? (
        <div className="space-y-4">
          {distributionData.map((item, index) => (
            <div key={index} className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>{item.name}</span>
                <span>{item.value}</span>
              </div>
              <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                <div 
                  className={`h-full ${item.color}`}
                  style={{ width: `${maxValue > 0 ? (item.value / maxValue) * 100 : 0}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-4">
          {filteredTrend.map((week, index) => (
            <div key={index} className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>{week.date}</span>
                <div className="flex gap-4">
                  <span className="text-green-500">{week.truePositive}</span>
                  <span className="text-red-500">{week.falsePositive}</span>
                  <span className="text-blue-500">{week.needsInvestigation}</span>
                </div>
              </div>
              <div className="h-2 bg-gray-700 rounded-full overflow-hidden flex">
                <div 
                  className="h-full bg-green-500"
                  style={{ width: `${maxValue > 0 ? (week.truePositive / maxValue) * 100 : 0}%` }}
                />
                <div 
                  className="h-full bg-red-500"
                  style={{ width: `${maxValue > 0 ? (week.falsePositive / maxValue) * 100 : 0}%` }}
                />
                <div 
                  className="h-full bg-blue-500"
                  style={{ width: `${maxValue > 0 ? (week.needsInvestigation / maxValue) * 100 : 0}%` }}
                />
              </div>
            </div>
          ))}
          <div className="flex justify-end gap-4 text-xs">
            <span className="text-green-500">True Positive</span>
            <span className="text-red-500">False Positive</span>
            <span className="text-blue-500">Needs Investigation</span>
          </div>
        </div>
      )}
    </div>
  );
};
